import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type SetStateAction,
} from 'react';

/**
 * Sorts all rows for which drag and drop should be disabled by default to either the start or end of the DataTable.
 *
 * @param props - The object containing the initial row order, the set of rows for which
 *                drag and drop should be disabled, and the position to which disabled rows should be
 *                sorted and locked.
 * @returns - An object containing the set of rows for which drag and drop should be disabled, the adapted row order
 *            with disabled rows sorted to the start or end of the table, and the function to update the row order
 *            on drag and drop.
 * @public
 */
export function useLockedRowOrder(props: {
  initialRowOrder: string[];
  lockedRows: string[];
  position?: 'start' | 'end';
}) {
  const { initialRowOrder, lockedRows, position = 'start' } = props;

  const getOrderedRows = useCallback(
    (rows: string[]) => {
      if (!position) {
        return rows;
      }
      const { locked, unlocked } = rows.reduce<{
        locked: string[];
        unlocked: string[];
      }>(
        (acc, row) => {
          lockedRows.includes(row)
            ? acc.locked.push(row)
            : acc.unlocked.push(row);
          return acc;
        },
        { locked: [], unlocked: [] },
      );

      return position === 'start'
        ? [...locked, ...unlocked]
        : [...unlocked, ...locked];
    },
    [lockedRows, position],
  );

  const [rowOrder, setRowOrderState] = useState(
    getOrderedRows(initialRowOrder),
  );

  const setRowOrder = useCallback(
    (updater: SetStateAction<string[]>) => setRowOrderState(updater),
    [],
  );

  // If the position or locked rows change, update the row order
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setRowOrder(getOrderedRows(rowOrder));

    // Only needs to run when the specified position or the locked rows change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockedRows, position]);

  const disableRowDragAndDrop = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (row: any) => lockedRows.includes(row.id),
    [lockedRows],
  );

  const rowOrdering = useMemo(
    () => ({
      enableDragAndDrop: true,
      disableRowDragAndDrop,
      lockDisabledRows: position,
    }),
    [disableRowDragAndDrop, position],
  );

  return {
    rowOrdering,
    rowOrder,
    setRowOrder,
  };
}
